import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';

import 'leaflet/dist/leaflet.css';

export interface ViewerPoint {
  lat: number;
  lng: number;
  count: number;
}

interface ViewerMapProps {
  points: ViewerPoint[];
  /** The listing's own coordinates, used to center the map when there are no points. */
  latitude: number | null;
  longitude: number | null;
}

const MAPBOX_STYLE_ID = 'mapbox/light-v11';

const MAPBOX_ATTRIBUTION = `
  &copy; <a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a>
  &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors`;

/**
 * Frames the map on the points once it has mounted.
 *
 * MapContainer needs a real `center` and `zoom` to establish its view. Passing
 * `bounds` with `center={undefined}` leaves its internal center at [0, 0], so
 * every later zoom re-anchors on the Gulf of Guinea and the map appears to pan
 * wildly sideways. Giving it a genuine center and fitting afterwards keeps the
 * view state consistent.
 */
function FitToPoints({ bounds }: { bounds: L.LatLngBounds | null }) {
  const map = useMap();

  useEffect(() => {
    if (!bounds) return;
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 11 });
  }, [map, bounds]);

  return null;
}

/**
 * Where renters were browsing from, as proportional circles. Visit coordinates
 * come from Cloudflare's geo headers and are rounded to two decimal places in
 * the rollup, so this is city-level at best - never a precise location.
 */
export function ViewerMap({ points, latitude, longitude }: ViewerMapProps) {
  const peak = useMemo(() => points.reduce((max, point) => Math.max(max, point.count), 0), [points]);

  const bounds = useMemo(() => {
    if (points.length < 2) return null;
    return L.latLngBounds(points.map((point) => [point.lat, point.lng] as L.LatLngTuple));
  }, [points]);

  // Weighted toward where the views actually are, so the opening frame is
  // sensible even before fitBounds runs.
  const center = useMemo<L.LatLngExpression>(() => {
    if (points.length > 0) {
      const total = points.reduce((sum, p) => sum + p.count, 0) || points.length;
      const lat = points.reduce((sum, p) => sum + p.lat * p.count, 0) / total;
      const lng = points.reduce((sum, p) => sum + p.lng * p.count, 0) / total;
      return [lat, lng];
    }
    return [latitude ?? 42.36, longitude ?? -71.06];
  }, [points, latitude, longitude]);

  if (!ENV.MAPBOX_ACCESS_TOKEN) {
    return <div className="analytics-empty">Map cannot be displayed. Missing Mapbox configuration.</div>;
  }

  if (points.length === 0) {
    return <div className="analytics-empty">No location data for this period.</div>;
  }

  return (
    <MapContainer
      center={center}
      zoom={points.length > 1 ? 6 : 9}
      minZoom={1}
      maxZoom={14}
      style={{ height: '320px', width: '100%' }}
      scrollWheelZoom={false}
    >
      <TileLayer
        attribution={MAPBOX_ATTRIBUTION}
        url={`https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_ID}/tiles/{z}/{x}/{y}@2x?access_token=${ENV.MAPBOX_ACCESS_TOKEN}`}
        tileSize={512}
        zoomOffset={-1}
      />
      <FitToPoints bounds={bounds} />

      {points.map((point) => (
        <CircleMarker
          key={`${point.lat},${point.lng}`}
          center={[point.lat, point.lng]}
          // Area, not radius, should scale with the count - otherwise a busy
          // city swamps the map. Floor of 5px keeps single views clickable.
          radius={peak > 0 ? 5 + Math.sqrt(point.count / peak) * 18 : 5}
          pathOptions={{ color: '#3459e6', fillColor: '#3459e6', fillOpacity: 0.35, weight: 1 }}
        >
          <Tooltip>
            {point.count} view{point.count === 1 ? '' : 's'}
          </Tooltip>
        </CircleMarker>
      ))}
    </MapContainer>
  );
}
